import type { ReactNode, RefObject } from 'react'
import {
  createContext,
  createRef,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import { useEffectOnce } from 'react-use'
import clsx from 'clsx'
import { autorun, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import type { Variants } from 'motion/react'
import { motion } from 'motion/react'
import { clamp, range } from 'remeda'
import { ErrorBoundary } from '@sentry/react'

import { useBackgroundJobPoller } from '@jotta/grpc-connect-client/useBackgroundJobPoller'
import { useBackgroundJobCleanup } from '@jotta/hooks/useBackgroundJobCleanup'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { ErrorPage } from '@jotta/ui/ErrorPage'
import {
  setCSSCustomProperty,
  setElementCSSCustomProperties,
} from '@jotta/ui/setCSSCustomProperty'
import SvgLogoHeader from '@jotta/ui/SvgLogo_headerLight'
import SvgMobile_logo_headerLight from '@jotta/ui/SvgMobile_logo_headerLight'
import SvgPowered_by_Jottacloud from '@jotta/ui/SvgPowered_by_Jottacloud'
import { useBrandCode, useBrandStore } from '@jotta/ui/useBrandTheme'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'
import { getBrandZIndex } from '@jotta/ui/zIndex'
import { cn, px } from '@jotta/utils/css'
import { env } from '@jotta/utils/env'

import styles from './AppLayout.module.scss'

export function PoweredByJotta({ className }: { className?: string }) {
  const brandCode = useBrandCode()
  const showPoweredByJottaSvg =
    brandCode === 'MEDIAMARKT' || brandCode === 'LETS_GO'
  return (
    (showPoweredByJottaSvg || env.isLocal) && (
      <div className={cn('pb-5 pt-2 opacity-65', className)}>
        <PlainBrandIcon icon={SvgPowered_by_Jottacloud} className="max-w-40" />
      </div>
    )
  )
}

const breakpointClassNames = range(0, 4)
  .map(n => `br-${n}`)
  .map((br, i, brs) => ({
    classNamesToAdd: [br, ...brs.slice(0, i + 1).map(v => `${v}-up`)],
    classNamesToRemove: [
      ...brs.filter(v => v !== br),
      ...brs.slice(i + 1).map(v => `${v}-up`),
    ],
  }))
function setBreakpointClass(index: number) {
  const i = clamp(index, {
    min: 0,
    max: breakpointClassNames.length - 1,
  })
  const { classNamesToAdd, classNamesToRemove } = breakpointClassNames[i]
  document.body.classList.add(...classNamesToAdd)
  document.body.classList.remove(...classNamesToRemove)
}

export const AppLayoutContext = createContext<{
  scrollElement: RefObject<HTMLDivElement | null>
}>({ scrollElement: createRef() })

export interface ApplayoutProps {
  setNavOpen?: (open: boolean) => void
  header?: ReactNode
  navbar?: ReactNode
  banner?: ReactNode
  moduleNav?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  overlay?: RefObject<HTMLDivElement | null>
  className?: string
}
export const AppLayout = observer<ApplayoutProps>(function AppLayout({
  children,
  header = null,
  navbar,
  banner,
  moduleNav,
  footer,
  overlay,
  className,
}) {
  const headerRef = useRef<HTMLElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const branding = useBrandStore()

  const showFullsizeLogo = useBreakpoint(Breakpoint.sm)
  const showNavbar = useBreakpoint(Breakpoint.lg) && Boolean(navbar)

  useBackgroundJobPoller()
  useBackgroundJobCleanup()

  const alertListVariants: Variants = {
    open: {
      opacity: 1,
    },
    closed: {
      opacity: 0,
    },
  }

  useEffectOnce(() =>
    autorun(() => {
      setCSSCustomProperty(
        '--bottom-toolbar-height',
        px(branding.bottomToolbarHeight),
      )
    }),
  )

  useEffectOnce(() =>
    autorun(() => {
      if (branding.isMobile) {
        document.body.classList.add('is-small')
        document.body.classList.remove('is-large')
      } else {
        document.body.classList.remove('is-small')
        document.body.classList.add('is-large')
      }
      if (branding.isTouchDevice()) {
        document.body.classList.add('is-touch')
      } else {
        document.body.classList.remove('is-touch')
      }
    }),
  )

  useEffectOnce(() =>
    autorun(() => {
      setBreakpointClass(branding.currentBreakpointIndex)
    }),
  )

  useEffectOnce(() =>
    autorun(() => {
      setElementCSSCustomProperties({
        '--visible-scrollbar-width': px(branding.visibleScrollBarWidth),
      })
    }),
  )

  useEffectOnce(() =>
    autorun(() => {
      setElementCSSCustomProperties({
        '--scrubber-width': branding.hasScrubber
          ? 'var(--scrubber-active-width, 0px)'
          : '0px',
      })
    }),
  )

  useLayoutEffect(() => {
    runInAction(() => {
      branding.headerHeight = headerRef.current!.getBoundingClientRect().height
    })
  }, [banner, branding, branding.viewportWidth])

  useEffect(() => {
    const element = containerRef.current
    if (element) {
      branding.setScrollElement(element)
      return () => {
        branding.setScrollElement(document.documentElement)
      }
    }
  }, [branding, containerRef])

  return (
    <div className="flex size-full flex-col overflow-hidden">
      <header
        ref={headerRef}
        className="left-0 top-0 z-appHeader flex flex-col"
      >
        {banner}
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-m3-primary"
            >
              <PlainBrandIcon
                icon={
                  showFullsizeLogo ? SvgLogoHeader : SvgMobile_logo_headerLight
                }
                height={40}
              />
            </a>
          </div>
          <div className="flex items-center gap-2">{header}</div>
        </div>
      </header>
      <div className="relative flex grow overflow-hidden lg:gap-3 lg:px-4 lg:pb-4">
        {showNavbar && (
          <div className="flex h-full shrink-0 flex-col justify-between overflow-y-auto rounded-xl bg-primary">
            {navbar}
            <PoweredByJotta className="mx-auto px-2" />
          </div>
        )}
        <div className="relative flex size-full flex-col overflow-x-auto lg:rounded-xl">
          {overlay ? (
            <div className="relative z-appOverlay flex size-full flex-none flex-col">
              {moduleNav}
              <div
                className="flex-1 overflow-y-auto overflow-x-hidden rounded-xl bg-primary pb-[50px] sm:p-4"
                ref={overlay}
              />
            </div>
          ) : (
            <>{moduleNav}</>
          )}
          <div
            ref={containerRef} // This ref must be set at this level of the DOM tree to ensure the timeline functions correctly
            className="grow overflow-y-auto overflow-x-hidden bg-primary lg:rounded-xl"
          >
            <div className="relative flex size-full flex-auto flex-col">
              <AppLayoutContext.Provider
                value={{ scrollElement: containerRef }}
              >
                <ErrorBoundary
                  fallback={error => <ErrorPage error={error} hideFooter />}
                >
                  <main
                    className={cn('shrink-0 grow lg:rounded-xl', className)}
                  >
                    {children}
                  </main>
                  {footer}
                </ErrorBoundary>
              </AppLayoutContext.Provider>
            </div>
          </div>
          <div
            className="fixed left-3 right-3 max-w-[400px] sm:right-1/2 md:right-1/4"
            style={{
              bottom: 'calc(var(--content-bottom) + var(--s3))',
              zIndex: getBrandZIndex('uploadStatus'),
            }}
          >
            <motion.div
              layout="position"
              variants={alertListVariants}
              initial="closed"
              animate="open"
              exit="closed"
              ref={branding.slots.uploadStatus}
            />
          </div>
          <motion.div
            layout="position"
            data-testid="AppLayoutButtons"
            transition={{ duration: 0 }}
            className="fixed z-fab mb-3 mr-4 flex h-[60px] w-auto items-end gap-3 self-end"
            style={{
              right:
                'calc(var(--content-right) + var(--removed-body-scroll-bar-size, 0px))',
              bottom: 'var(--content-bottom)',
            }}
          >
            <motion.div
              data-testid="AppLayoutButtonsFab"
              layout="position"
              ref={branding.slots.actionButton}
            />
          </motion.div>
          <div
            data-testid="AppLayoutScrubber"
            sx={{
              position: 'fixed',
              right: 0,
              top: '140px',
              bottom: 'var(--content-bottom)',
              width: 'var(--scrubber-width)',
              zIndex: getBrandZIndex('timelineScrubber'),
            }}
            ref={branding.slots.timelineScrubber}
          />

          <motion.div
            layout="position"
            data-has-toolbar={branding.hasBottomToolbar}
            data-testid="AppLayoutBottomToolbar"
            transition={{ bounce: 0, duration: 0.2 }}
            animate={{
              opacity: branding.hasBottomToolbar ? 1 : 0,
            }}
            className={cn(styles.toolbarBottom, 'z-appMainBottomToolbar')}
          >
            <div
              data-slot="bottomstatus"
              className={cn(styles.toolbarBottomSlot, 'rounded-xl bg-document')}
              ref={branding.slots.bottomStatusBar}
              data-testid="bottomStatusBar"
            />
          </motion.div>
        </div>
      </div>
      {/* <NavigationBlocker /> */}
    </div>
  )
})
